<template>
  <div>
    <b-dropdown
      class="float-right"
      variant="link"
      toggle-class="text-decoration-none"
      no-caret
      right
    >
      <template #button-content>
        <span class="svg-icon svg-icon-lg">
          <inline-svg src="media/svg/icons/General/Menu.svg" />
        </span>
      </template>
      <b-dropdown-item @click="CSV()">CSV</b-dropdown-item>
      <b-dropdown-item @click="saveImg()">PNG</b-dropdown-item>
    </b-dropdown>
    <br /><br /><br />
    <v-chart ref="chartRef" class="chart" :option="option" />
  </div>
</template>

<script>
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart, LineChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from "echarts/components";
import VChart, { THEME_KEY } from "vue-echarts";

import "echarts/lib/component/grid";

use([
  CanvasRenderer,
  BarChart,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent
]);

export default {
  name: "OldCustomersLineChart",
  components: {
    VChart
  },
  provide: {
    [THEME_KEY]: "light"
  },
  props: {
    data: {
      type: Object,
      default: null
    },
    isProPlan: {
      type: Boolean,
      default: false
    },
    campaignId: {
      type: [String, Number],
      default: null
    },
    filterFrom: {
      type: Date,
      default: null
    },
    filterTo: {
      type: Date,
      default: null
    }
  },
  data() {
    return {
      option: {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow"
          },
          formatter: (params) => {
            const tooltipItems = Array.isArray(params) ? params : [params];

            if (!tooltipItems.length) {
              return "";
            }

            const lines = [
              tooltipItems[0].axisValueLabel || tooltipItems[0].name
            ];

            tooltipItems.forEach((item) => {
              const value = Array.isArray(item.value)
                ? item.value[1]
                : item.value;
              const suffix = item.seriesType === "line" ? "%" : "";
              lines.push(
                `${item.marker} ${item.seriesName} : ${value}${suffix}`
              );
            });

            return lines.join("<br/>");
          }
        },
        legend: {
          bottom: "bottom",
          type: "scroll",
          height: "200"
        },
        grid: {
          left: "10%",
          right: "10%",
          bottom: "20%",
          containLabel: true
        },
        xAxis: [
          {
            name: `${this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CAMPAIGN_LINE_X"
            )}`,
            type: "category",
            data: [],
            axisLabel: {
              formatter: function (value) {
                return value;
              },
              rotate: 0
            }
          }
        ],
        yAxis: [
          {
            name: `${this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.OLDCUSTOMERS_LINE_Y"
            )}`,
            type: "value"
          },
          {
            name: "อัตราการกลับมา (%)",
            type: "value",
            min: 0,
            max: 100,
            position: "right",
            axisLabel: {
              formatter: "{value}%"
            },
            splitLine: {
              show: false
            },
            show: false
          }
        ],
        series: [],
        color: ["#1f5fb8", "#e67e22", "#00e396"],
        backgroundColor: "white"
      }
    };
  },
  mounted() {
    this.applyChartData();
  },
  watch: {
    data: {
      handler() {
        this.applyChartData();
      },
      deep: true
    }
  },
  methods: {
    normalizeSeriesConfig(element, defaultType) {
      const seriesType = element && element.type ? element.type : defaultType;

      if (seriesType === "line") {
        const lineColor =
          element && element.itemStyle && element.itemStyle.color
            ? element.itemStyle.color
            : "#e67e22";
        const showLabel =
          element && element.showLabel !== undefined ? element.showLabel : true;

        return {
          name: element.name,
          type: "line",
          yAxisIndex: element.yAxisIndex !== undefined ? element.yAxisIndex : 1,
          smooth: true,
          symbol: "circle",
          symbolSize: 8,
          lineStyle: {
            color: lineColor,
            width: 3,
            ...(element.lineStyle || {})
          },
          itemStyle: {
            color: lineColor,
            ...(element.itemStyle || {})
          },
          label: {
            show: showLabel,
            position: "top",
            color: lineColor,
            formatter: ({ value }) => `${value}%`
          },
          emphasis: {
            focus: "series"
          },
          data: element.data || []
        };
      }

      return {
        name: element.name,
        type: "bar",
        barMaxWidth: 44,
        itemStyle: {
          borderRadius: 0,
          ...(element.itemStyle || {})
        },
        emphasis: {
          focus: "series"
        },
        data: element.data || []
      };
    },
    getAllChartSeries() {
      const primarySeries = Array.isArray(this.data && this.data.series)
        ? this.data.series.map((element) => {
            return this.normalizeSeriesConfig(element, element.type || "bar");
          })
        : [];
      const extraLineSeries = Array.isArray(this.data && this.data.lineSeries)
        ? this.data.lineSeries
        : Array.isArray(this.data && this.data.returnRateSeries)
        ? this.data.returnRateSeries
        : [];

      const normalizedExtraLineSeries = extraLineSeries
        .map((element) => this.normalizeSeriesConfig(element, "line"))
        .filter((lineItem) => {
          return !primarySeries.some(
            (seriesItem) => seriesItem.name === lineItem.name
          );
        });

      return primarySeries.concat(normalizedExtraLineSeries);
    },
    applyChartData() {
      const categories = Array.isArray(this.data && this.data.categories)
        ? this.data.categories
        : [];
      const seriesData = this.getAllChartSeries();
      const hasLineSeries = seriesData.some(
        (element) => element.type === "line"
      );

      this.option.xAxis[0].data = categories;
      this.option.series = seriesData;
      this.option.yAxis[1].show = hasLineSeries;
      this.option.grid.right = hasLineSeries ? "12%" : "10%";
    },
    csvEscapeCell(val) {
      const s = String(val == null ? "" : val);
      if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    },
    apiCatchMessage(error) {
      if (error && error.message) return error.message;
      if (error && error.response && error.response.data) {
        const d = error.response.data;
        if (typeof d === "string") return d;
        if (d.message) return d.message;
        if (d.error) return d.error;
      }
      return error != null ? String(error) : "Unknown error";
    },
    async CSV() {
      const exportCategories = this.option.xAxis[0].data || [];
      const exportSeries = this.option.series || [];

      if (!exportCategories.length || !exportSeries.length) {
        return;
      }

      let headers = [
        `${this.$t(
          "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CAMPAIGN_LINE_X"
        )}`
      ];

      for (let index = 0; index < exportSeries.length; index++) {
        const element = exportSeries[index];
        headers.push(element.name);
      }

      let contents = [];
      for (let index = 0; index < exportCategories.length; index++) {
        const element = exportCategories[index];
        contents.push([this.csvEscapeCell(element)]);
      }

      for (let index = 0; index < exportSeries.length; index++) {
        const element = exportSeries[index];
        for (let index2 = 0; index2 < element.data.length; index2++) {
          const element2 = element.data[index2];
          contents[index2].push(element2);
        }
      }

      let csvData = [];
      csvData.push(headers);
      for (let index = 0; index < contents.length; index++) {
        const element = contents[index];
        csvData.push(element);
      }

      let lineArray = [];
      csvData.forEach(function (infoArray, index) {
        let line = infoArray.join(",");
        lineArray.push(index == 0 ? "\uFEFF" + line : line);
      });
      let csvContent = lineArray.join("\n");

      let blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      let link = document.createElement("a");

      if (link.download !== undefined) {
        link.setAttribute("href", window.URL.createObjectURL(blob));
        link.setAttribute(
          "download",
          `${this.$t(
            "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.OLDCUSTOMERS_LINE_Y"
          )}.csv`
        );
        link.setAttribute("hidden", true);
      } else {
        link.setAttribute("href", "#");
      }
      link.click();
    },

    saveImg() {
      const chartRef = this.$refs.chartRef;
      const chartInstance =
        chartRef && chartRef.chart ? chartRef.chart : chartRef;
      let imageDataUrl = null;

      if (chartInstance && typeof chartInstance.getDataURL === "function") {
        imageDataUrl = chartInstance.getDataURL({
          type: "png",
          pixelRatio: 2,
          backgroundColor: "#ffffff"
        });
      }

      if (!imageDataUrl) {
        const canvas = this.$el.querySelector("canvas");
        if (canvas) {
          imageDataUrl = canvas.toDataURL("image/png");
        }
      }

      if (imageDataUrl) {
        let tempA = document.createElement("a");
        tempA.download = `${this.$t(
          "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.OLDCUSTOMERS_LINE_Y"
        )}.png`;
        tempA.href = imageDataUrl;
        document.body.appendChild(tempA);
        tempA.click();
        tempA.remove();
      }
    }
  }
};
</script>

<style scoped>
.chart {
  height: 600px;
}
</style>

<style>
body {
  margin: 0;
}
</style>
